import type { Guid } from '@ael/core-api';

import type { AssetScope } from '../client-state/mediaLibraryTypes';

/**
 * Keyed by company at the root: the library shares its host's cache, so these keys are the only
 * thing keeping two tenants' libraries apart. The company comes off the scoped client, which is
 * the one source that cannot disagree with the calls being made.
 */
export const mediaLibraryKeys = (companyId: Guid) => ({
  all: ['media-library', companyId] as const,
  folderTree: () => ['media-library', companyId, 'folder-tree'] as const,
  assets: () => ['media-library', companyId, 'assets'] as const,
  assetsByScope: (scope: AssetScope) => ['media-library', companyId, 'assets', scope] as const,
  assetDetails: () => ['media-library', companyId, 'asset-detail'] as const,
  assetDetail: (assetId: string) => ['media-library', companyId, 'asset-detail', assetId] as const,
  /** Under the detail, so removing a deleted asset's detail takes its downloaded content with it. */
  assetContent: (assetId: string, mediaUrl: string) =>
    ['media-library', companyId, 'asset-detail', assetId, 'content', mediaUrl] as const,
});

/**
 * The key types, named so the query factories can state them.
 *
 * Those factories annotate their return types rather than letting them be inferred, and this is
 * the note explaining why, because these types exist for it: TanStack Query ships its types as a
 * tsup rollup that its package `exports` map does not expose, so a declaration emitted from an
 * inferred `queryOptions(...)` or `mutationOptions(...)` result has to point at a file no consumer
 * can resolve -- TS2742, "the inferred type ... cannot be named". It only surfaces in the apps:
 * this library is consumed through a project reference, so `tsc --build` in manager, student or
 * Panomio emits its declarations, while type-checking the library alone emits nothing.
 *
 * Naming `UseQueryOptions`/`UseMutationOptions` through the public entry keeps those declarations
 * portable, and the key has to be named with them -- React Query treats it as invariant, so an
 * options type widened to `QueryKey` rejects a `refetchInterval` or `enabled` callback, both of
 * which are handed the whole query. Deriving the keys here keeps the shapes in one place: change a
 * key above and these follow.
 */
type MediaLibraryKeys = ReturnType<typeof mediaLibraryKeys>;
export type FolderTreeKey = ReturnType<MediaLibraryKeys['folderTree']>;
export type AssetsByScopeKey = ReturnType<MediaLibraryKeys['assetsByScope']>;
export type AssetDetailKey = ReturnType<MediaLibraryKeys['assetDetail']>;
